import type { ExtensionApi } from './extension';
import type DEFINE from '../util/DEFINE';
import type { Option as StandardChartOption, StandardChartIns } from '../helper/standardChart';

/**
 * 模型训练信息
 */
export interface VisInfo {
  /** 组件唯一 id */
  id: string;
  /** 组件名称，英文 */
  name_en: string;
  /** 组件名称，中文 */
  name_zh: string;
  /** 分类标签（AI 模型训练依赖） */
  types_en: string[];
  /** 可视化目标，英文（AI 模型训练依赖） */
  purpose_en: string[];
  /** 可视化目标，中文（AI 模型训练依赖） */
  purpose_zh: string[];
  /**
   * （AI 模型训练依赖）
   * @example ['echarts','aime','f10'] */
  standers: string;
  /** （AI 模型训练依赖） */
  rules?: unknown;
  /** 数据特征参数（AI 模型训练依赖） */
  parameters: {
    [key: string]: {
      /** 枚举该字段值可接受的数据类型 */
      itemType: (typeof DEFINE)[keyof typeof DEFINE][];
      /** 该字段的集合类型 */
      category: (typeof DEFINE)[keyof typeof DEFINE];
      /** 优先类型 */
      priority?: (typeof DEFINE)[keyof typeof DEFINE];
      /** 可选字段 */
      optional?: boolean;
    };
  };
  /** 额外辅助信息 */
  extra?: {
    /** 是否不被用来训练 LLM */
    isNotTrainedByLLM: boolean;
  };
}

export interface Layer {
  type: VisInfo['id'];
  /** 普通的层，不依赖底层库 */
  isNormalLayer?: boolean;
  /** 底层依赖于标准范式图表库 */
  isStandardChartLayer?: boolean;
  /** 底层依赖于行情图组件 */
  isHXKLineLayer?: boolean;
  /** 底层依赖于第三方组件 */
  isExternalLayer?: boolean;
  /** 独立的层，单独渲染，不能和其它层合并共用 x 轴, y 轴等组件 */
  isIndependentLayer?: boolean;
  /** 渲染函数 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render: (api: ExtensionApi, ...args: any[]) => void;
  /** 可视化信息，模型训练需要 */
  visInfo: VisInfo;
  /** 依赖的CDN资源 */
  cdn?: string | string[];
}

export interface LayerSpec {
  type: Layer['type'];
  /** 使用的数据集索引，默认为 0 */
  dataIndex?: number;
  /** encoding内的字段映射是否有效 */
  __isEncodingValid?: boolean;
}

// * ----------------------------------------------------------------

export interface NormalLayerSpec extends LayerSpec {}

export interface NormalLayer<Spec extends NormalLayerSpec = NormalLayerSpec> extends Layer {
  /** 返回一个销毁资源的方法 */
  render: (api: ExtensionApi, spec: Spec, $dom: HTMLElement) => () => void;
}

export function defineNormalLayer<Spec extends NormalLayerSpec = NormalLayerSpec>(
  layer: HXKLineLayer<Spec>
) {
  return { ...layer, isNormalLayer: true, isIndependentLayer: true };
}

// * ----------------------------------------------------------------

type Dimension =
  | 'x'
  | 'y'
  | 'z'
  | 'size'
  | 'color'
  | 'name'
  | 'num'
  | 'compare'
  | 'target'
  | 'date'
  | 'companyName'
  | 'companyDescribe'
  | 'unit';

type SeriesOption = Exclude<StandardChartOption['series'], unknown[]>;

export interface StandardChartLayerSpec extends LayerSpec, SeriesOption {
  // * 数据编码的参数
  encoding: { [dim in Dimension]?: string | string[] };
  // * ECharts 的配置项
  // * 不同组件的自定义配置，以 dv 前缀命名
}

/**
 * @see https://echarts.apache.org/zh/option.html
 */
export type StandardChartLayerRenderResult = StandardChartOption & {
  legend: {
    data: ConstructorParameters<ExtensionApi['ui']['Legend']['Legend']>[1]['data'];
    layout: 'all' | 'scroll' | 'pagination';
  };
  xAxis: {
    /** 使用的数据集 */
    __dataIndex: number;
    /** 使用的数据集字段 */
    __encodeField: string;
    type: 'category' | 'value';
    name: string;
    data?: string[];
  }[];
  yAxis: {
    /** 使用的数据集 */
    __dataIndex: number;
    /** 使用的数据集字段 */
    __encodeField: string[];
    type: 'category' | 'value';
    name: string;
    data?: string[];
  }[];
  series: { type: string; name?: string; data: unknown[]; itemStyle?: { color?: string } }[];
};

export type StandardChartLayerCustomRender = (renderApi: {
  /** 合并主题配置 */
  mergeChartOptionProxy: <T = StandardChartOption>(option: T) => T;
  /** 请按 (时间轴 -> 图例 -> 图表) 的顺序进行渲染 */
  renderTimelineProxy: (data: string[]) => unknown;
  /** 请按 (时间轴 -> 图例 -> 图表) 的顺序进行渲染 */
  renderLegendProxy: (data: StandardChartLayerRenderResult['legend']['data']) => unknown;
  /** 请按 (时间轴 -> 图例 -> 图表) 的顺序进行渲染 */
  renderChartProxy: <T = StandardChartOption>(
    renderParams: { option: T },
    initParams?: { useECharts?: boolean; domContainer?: HTMLElement; theme?: string }
  ) => StandardChartIns;
  /** 重新设置当前周期的配置，并触发legend的事件绑定 */
  cacheRenderOptionProxy: <T = StandardChartOption>(option: T) => void;
  /** 获取当前图表（合并后）的配置 */
  getRenderOption: () => StandardChartOption;
  /** 解析option中回调函数 */
  optionCallbackProxy: <T = StandardChartOption>(option: T) => void;
}) => void;

export interface StandardChartLayer<Spec extends StandardChartLayerSpec = StandardChartLayerSpec>
  extends Layer {
  /** 是否动态图表，需要时间轴 */
  withTimeline?: boolean;
  /**
   * 图表的渲染函数
   *
   * ! 如果与主流程差异较大，可通过返回一个自定义渲染的函数来实现自己的逻辑，仅允许 `isIndependentLayer: true` 的图层实现自定义渲染逻辑
   */
  render: (
    api: ExtensionApi,
    spec: Spec,
    $dom: HTMLElement
  ) => Partial<StandardChartLayerRenderResult> | StandardChartLayerCustomRender;
}

export function defineStandardChartLayer<
  Spec extends StandardChartLayerSpec = StandardChartLayerSpec
>(layer: StandardChartLayer<Spec>) {
  return { ...layer, isStandardChartLayer: true };
}

// * ----------------------------------------------------------------

export interface HXKLineLayerSpec extends LayerSpec {}

export interface HXKLineLayer<Spec extends HXKLineLayerSpec = HXKLineLayerSpec> extends Layer {
  /** 返回一个销毁资源的方法 */
  render: (api: ExtensionApi, spec: Spec, $dom: HTMLElement, ...rest: any[]) => () => void;
}

export function defineHXKLineLayer<Spec extends HXKLineLayerSpec = HXKLineLayerSpec>(
  layer: HXKLineLayer<Spec>
) {
  return { ...layer, isHXKLineLayer: true, isIndependentLayer: true };
}

export interface ExternalLayerSpec extends LayerSpec {}

export interface ExternalLayer<Spec extends ExternalLayerSpec = ExternalLayerSpec> extends Layer {
  render: (api: ExtensionApi, spec: Spec, $dom: HTMLElement) => () => void;
}

export function defineExternalLayer<Spec extends ExternalLayerSpec = ExternalLayerSpec>(
  layer: ExternalLayer<Spec>
) {
  return { ...layer, isExternalLayer: true, isIndependentLayer: true };
}
